import { useQueryClient } from "@tanstack/react-query";
import { Building2, LogOut } from "lucide-react";
import { FC, useCallback } from "react";
import { FormattedMessage } from "react-intl";
import { useNavigate } from "react-router-dom";
import { Organization } from "~/api/model";
import { useListOrganizations } from "~/api/organizations/organizations";
import {
  getGetCurrentUserQueryKey,
  useGetCurrentUser,
} from "~/api/users/users";
import { Avatar, AvatarFallback, AvatarImage } from "~/components/ui/avatar";
import { Button } from "~/components/ui/button";
import { SKY_SELECTED_ORGANIZATION_ID } from "~/constants";
import { removeAllTokens, ROUTES } from "~/helpers";
import { useAuthStore } from "~/stores/authStore";

export const OrgSelectionPage: FC = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: organizations } = useListOrganizations();
  const { data: currentUser } = useGetCurrentUser();
  const orgsCount = organizations?.length || 0;
  const homeOrgs = organizations?.filter(
    (org) => !currentUser?.guestInOrganizations?.includes(org.id!)
  );

  const navigateToOrg = useCallback(
    (organizationId: string) => {
      localStorage.setItem(SKY_SELECTED_ORGANIZATION_ID, organizationId);
      navigate(ROUTES.CASES({ organizationId }));
    },
    [navigate]
  );

  const onLogout = async () => {
    removeAllTokens();
    useAuthStore.getState().setImpersonating(false);
    queryClient.setQueryData(getGetCurrentUserQueryKey(), null);
    await queryClient.cancelQueries();
    queryClient.clear();
    navigate(ROUTES.LOGIN);
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-4">
      {organizations && (
        <>
          <div className="w-full max-w-md space-y-6">
            {orgsCount == 0 && (
              <>
                <div className="space-y-2 text-center">
                  <p className="text-xl">
                    <FormattedMessage
                      id="orgSelection.deactivated"
                      defaultMessage="Your account was deactivated. Please reach out to support@sky-tech.ai"
                    />
                  </p>
                </div>
                <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
                  {homeOrgs?.map((org) => (
                    <OrgItem key={org.id} org={org} onSelect={navigateToOrg} />
                  ))}
                </div>
              </>
            )}
            {orgsCount > 0 && (
              <>
                <div className="space-y-2 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <Building2 className="h-6 w-6" />
                    <h1 className="text-2xl font-semibold tracking-tight">
                      <FormattedMessage
                        id="orgSelection.title"
                        defaultMessage="Select Organization"
                      />
                    </h1>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    <FormattedMessage
                      id="orgSelection.description"
                      defaultMessage="Choose which organization you'd like to work in."
                    />
                  </p>
                </div>

                <div className="flex flex-col gap-2 max-h-[400px] overflow-y-auto">
                  {homeOrgs?.map((org) => (
                    <OrgItem key={org.id} org={org} onSelect={navigateToOrg} />
                  ))}
                </div>
              </>
            )}
            <div className="flex justify-center pt-2">
              <Button variant="ghost" className="gap-2" onClick={onLogout}>
                <LogOut className="h-4 w-4" />
                <FormattedMessage
                  id="orgSelection.logout"
                  defaultMessage="Log out"
                />
              </Button>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

const OrgItem: FC<{
  org: Organization;
  onSelect: (organizationId: string) => void;
}> = ({ org, onSelect }) => {
  const initials = (org.name ?? "")
    .split(" ")
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <button
      type="button"
      onClick={() => onSelect(org.id!)}
      className="flex items-center gap-3 w-full rounded-lg border p-3 text-left transition-colors hover:bg-accent focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-ring"
    >
      <Avatar className="h-9 w-9">
        {org.logoUrl && org.logoUrl.trim() !== "https://placehold.co/100" && (
          <AvatarImage src={org.logoUrl} alt={org.name} />
        )}
        <AvatarFallback>{initials || "?"}</AvatarFallback>
      </Avatar>
      <span className="text-sm font-medium truncate">{org.name}</span>
    </button>
  );
};
